import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";

const fmtDt = (iso) => new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function FinanceHistory() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [params, setParams] = useSearchParams();
  const [accts, setAccts] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({
    account_id: params.get("account") || "",
    direction: "", movement_type: "", date_from: "", date_to: "", q: "", page: 1,
  });

  useEffect(() => { if (isOwner) api.get("/accounts").then(({ data }) => setAccts(data)).catch(() => {}); }, [isOwner]);

  const load = useCallback(() => {
    const qp = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) qp[k] = v; });
    api.get("/performance/movements", { params: qp }).then(({ data }) => setData(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [f]);
  useEffect(() => { if (isOwner) load(); }, [load, isOwner]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v, page: k === "page" ? v : 1 }));

  if (user && !isOwner) {
    return (<div className="app-shell"><AppTopBar active="accounts" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="accounts" />
      <main className="content">
        <Link to="/accounts" className="back-link" data-testid="back-to-accounts">← Kembali ke Master Account</Link>
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        <section className="panel" data-testid="finance-history-panel">
          <h2 className="panel-title">Riwayat Kas / Bank</h2>

          {data && f.account_id && (
            <div className="meta-grid" data-testid="account-summary" style={{ marginBottom: 8 }}>
              <div><span className="meta-label">Account</span><span className="mono" data-testid="summary-account">{data.account_name}</span></div>
              <div><span className="meta-label">Saldo Saat Ini</span><span className="mono" data-testid="summary-current-balance">{formatIDR(data.current_balance)}</span></div>
              <div><span className="meta-label">Total Masuk (filter)</span><span className="mono" data-testid="summary-total-in">{formatIDR(data.total_in)}</span></div>
              <div><span className="meta-label">Total Keluar (filter)</span><span className="mono" data-testid="summary-total-out">{formatIDR(data.total_out)}</span></div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
            <div><label className="field-label">Account</label>
              <select className="field-input" value={f.account_id} onChange={(e) => set("account_id", e.target.value)} data-testid="filter-account">
                <option value="">Semua Account</option>
                {accts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
              </select></div>
            <div><label className="field-label">Arah</label>
              <select className="field-input narrow" value={f.direction} onChange={(e) => set("direction", e.target.value)} data-testid="filter-direction">
                <option value="">Semua</option><option value="IN">IN</option><option value="OUT">OUT</option>
              </select></div>
            <div><label className="field-label">Jenis</label>
              <select className="field-input" value={f.movement_type} onChange={(e) => set("movement_type", e.target.value)} data-testid="filter-type">
                <option value="">Semua Jenis</option>
                {(data?.movement_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className="field-label">Dari</label><input className="field-input narrow" type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" /></div>
            <div><label className="field-label">Sampai</label><input className="field-input narrow" type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" /></div>
            <div><label className="field-label">Cari (MS/PUR/account)</label><input className="field-input" placeholder="cari referensi" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="filter-search" /></div>
          </div>

          <div className="table-scroll">
            <table className="data-table" data-testid="movements-table">
              <thead><tr><th>Waktu</th><th>Account</th><th>Arah</th><th>Nominal</th><th>Jenis</th><th>Referensi</th><th>User</th><th>Saldo</th></tr></thead>
              <tbody>
                {(!data || data.movements.length === 0) && <tr><td colSpan={8} className="empty-cell" data-testid="movements-empty">Tidak ada pergerakan.</td></tr>}
                {data && data.movements.map((m) => (
                  <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                    <td>{fmtDt(m.created_at)}</td>
                    <td className="mono">{m.account_name}</td>
                    <td><span className={`status-pill ${m.direction === "IN" ? "on" : "off"}`} data-testid={`dir-${m.id}`}>{m.direction}</span></td>
                    <td className="mono" data-testid={`amount-${m.id}`}>{formatIDR(m.amount)}</td>
                    <td className="mono">{m.movement_type}</td>
                    <td data-testid={`ref-${m.id}`}>{m.reference}</td>
                    <td className="mono">{m.user ? m.user.slice(0, 8) : "-"}</td>
                    <td className="mono" data-testid={`balance-after-${m.id}`}>{formatIDR(m.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.total_pages > 1 && (
            <div className="form-actions" style={{ marginTop: 10, alignItems: "center", gap: 12 }} data-testid="pagination">
              <button className="btn-mini" disabled={f.page <= 1} onClick={() => set("page", f.page - 1)} data-testid="page-prev">← Sebelumnya</button>
              <span className="hint" data-testid="page-info">Halaman {data.page} / {data.total_pages} · {data.total} data</span>
              <button className="btn-mini" disabled={f.page >= data.total_pages} onClick={() => set("page", f.page + 1)} data-testid="page-next">Berikutnya →</button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
